import { httpService } from './api/http';
import { resolveGatewayBackedServiceBaseUrl } from './serviceBaseUrl';

const GEO_SERVICE_API_BASE = resolveGatewayBackedServiceBaseUrl(
    process.env.REACT_APP_GEO_SERVICE_URL,
);

const GEO_ENDPOINTS = Object.freeze({
    POINTS: `${GEO_SERVICE_API_BASE}/api/maps/v1/points`,
    EVENTS: `${GEO_SERVICE_API_BASE}/api/maps/v1/events`,
    LAYERS: `${GEO_SERVICE_API_BASE}/api/maps/v1/layers`,
    FIELD_CATALOG: `${GEO_SERVICE_API_BASE}/api/maps/v1/layers/field-catalog`,
});

function buildPointsQuery(options = {}) {
    const bounds = options.bounds;
    const params = new URLSearchParams();
    const types = Array.isArray(options.types)
        ? Array.from(new Set(options.types
            .map((type) => (typeof type === 'string' ? type.trim().toUpperCase() : ''))
            .filter(Boolean)))
        : null;
    if (types) {
        types.forEach((type) => params.append('types', type));
    }
    const center = options.center;
    if (center && typeof center === 'object') {
        const centerLat = center.lat ?? center.latitude;
        const centerLng = center.lng ?? center.lon ?? center.longitude;
        if ([centerLat, centerLng].every((value) => typeof value === 'number' && Number.isFinite(value))) {
            params.set('centerLat', centerLat.toString());
            params.set('centerLng', centerLng.toString());
        }
    }
    if (!bounds || typeof bounds !== 'object') {
        if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
            params.set('limit', String(Math.max(1, Math.floor(options.limit))));
        }
        const noBoundsQuery = params.toString();
        return noBoundsQuery ? `?${noBoundsQuery}` : '';
    }
    const { north, south, east, west } = bounds;
    if ([north, south, east, west].every((value) => typeof value === 'number' && Number.isFinite(value))) {
        params.set('north', north.toString());
        params.set('south', south.toString());
        params.set('east', east.toString());
        params.set('west', west.toString());
    }
    if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.max(1, Math.floor(options.limit))));
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

async function fetchPoints(options = {}) {
    const query = buildPointsQuery(options);
    return httpService.get(`${GEO_ENDPOINTS.POINTS}${query}`, options);
}

async function fetchEvents(options = {}) {
    const query = buildPointsQuery(options);
    return httpService.get(`${GEO_ENDPOINTS.EVENTS}${query}`, options);
}

async function createPoint(payload, options = {}) {
    return httpService.post(GEO_ENDPOINTS.POINTS, payload, options);
}

async function updatePoint(id, payload, options = {}) {
    if (typeof id !== 'string' && typeof id !== 'number') {
        throw new Error('Map point id is required to update a marker');
    }
    const trimmedId = String(id).trim();
    if (!trimmedId) {
        throw new Error('Map point id is required to update a marker');
    }
    return httpService.put(`${GEO_ENDPOINTS.POINTS}/${encodeURIComponent(trimmedId)}`, payload, options);
}

async function fetchLayers(options = {}) {
    return httpService.get(GEO_ENDPOINTS.LAYERS, options);
}

async function updateLayer(id, payload, options = {}) {
    if (typeof id !== 'string' || id.trim().length === 0) {
        throw new Error('Layer id is required to update a map layer');
    }
    return httpService.put(`${GEO_ENDPOINTS.LAYERS}/${encodeURIComponent(id.trim())}`, payload, options);
}

async function createCustomLayer(payload, options = {}) {
    return httpService.post(GEO_ENDPOINTS.LAYERS, payload, options);
}

async function fetchFieldCatalog(type, options = {}) {
    if (typeof type !== 'string' || type.trim().length === 0) {
        throw new Error('Type is required to fetch field catalog');
    }
    const params = new URLSearchParams({ type: type.trim() });
    return httpService.get(`${GEO_ENDPOINTS.FIELD_CATALOG}?${params.toString()}`, options);
}

async function reorderLayers(orderedIds, options = {}) {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
        throw new Error('Ordered layer ids are required to reorder layers');
    }
    return httpService.put(`${GEO_ENDPOINTS.LAYERS}/reorder`, orderedIds, options);
}

async function deleteLayer(id, options = {}) {
    if (typeof id !== 'string' || id.trim().length === 0) {
        throw new Error('Layer id is required to delete a map layer');
    }
    return httpService.delete(`${GEO_ENDPOINTS.LAYERS}/${encodeURIComponent(id.trim())}`, options);
}

export const geoService = Object.freeze({
    endpoints: GEO_ENDPOINTS,
    fetchPoints,
    fetchEvents,
    createPoint,
    updatePoint,
    fetchLayers,
    updateLayer,
    createCustomLayer,
    deleteLayer,
    reorderLayers,
    fetchFieldCatalog,
});
